import ButtonCommon from '@components/button-common/ButtonCommon';
import InputPhone from '@components/input-phone/InputPhone';
import LoaderCircle from '@components/loader-circle/LoaderCircle';
import { PortalSimple } from '@components/portal-simple/PortalSimple';
import { yupResolver } from '@hookform/resolvers/yup';
import { api } from '@lib/api/api';
import { COOKIE_SECURE_FORMA_29680, NEW_FORM_FIELDS_FORMA_34648 } from '@lib/flags';
import { policyFormSelector } from '@lib/store/slices/ProjectSlice';
import { formatPrice } from '@lib/tools/_format-price';
import { CookieStorage } from '@lib/tools/helpers/CookieStorage';
import { UNIVERSAL_ADMIN } from '@lib/tools/helpers/constants';
import { SafeHtmlRenderer } from '@lib/tools/helpers/SafeHtmlRenderer';
import { useOnClickOutside } from '@lib/tools/hooks/useOnClickOutside';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';
import axios from 'axios';
import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CheckBox } from '../checkbox/CheckBox';
import { DropDownCalc } from '../dropdown/Dropdown';
import styles from './FormMortgageModal.module.scss';
import { formatBirthDate, fullPhonePattern, schema } from './schema';
const buildComment = (data, modalFormData) => {
  const {
    realtyCost,
    initialPayment,
    period,
    type,
    programName = 'Не указана'
  } = modalFormData;
  const initialPaymentPercent = realtyCost ? Math.round(initialPayment / realtyCost * 100) : 0;
  const lines = [...[NEW_FORM_FIELDS_FORMA_34648 ? [`Заявка с формы: Заявка по ипотеке`, `Комментарий: Сайт: Моментс`].join('\n') : []], `Стоимость объекта: ${realtyCost}₽`, `Первоначальный взнос: ${initialPayment}₽ (${initialPaymentPercent}%)`, `Срок кредита: ${period} лет`, `Дата рождения: ${data.birthDate}`, `Вид ипотеки: ${type}`, `Название программы: ${programName}`, `Гражданство РФ: ${data.isCitizen ? 'Да' : 'Нет'}`, `Состоит в браке: ${data.isMarried ? 'Да' : 'Нет'}`, `Email: ${data.email || '—'}`, `Есть одобренная ипотека: ${data.hasApprovedMortgage ? 'Да' : 'Нет'}`];
  if (data.hasApprovedMortgage) {
    lines.push(`Банк: ${data.bankName || ''}`, `Сумма одобренной ипотеки: ${data.mortgageSum || ''}`);
  }
  return lines.join('\n');
};
export const FormMortgageModal = ({
  isVisible,
  onClose,
  onAfterClose,
  modalFormData,
  programTypes,
  formName = 'mortgageFinchForm'
}) => {
  const [captchaToken, setCaptchaToken] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [formData, setFormData] = useState(null);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const ref = useRef();
  useOnClickOutside(ref, () => onClose());
  const policyForm = useSelector(policyFormSelector);
  const {
    control,
    formState: {
      errors,
      isValid
    },
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      price: '',
      firstPaymentAmount: '',
      firstPaymentPercent: '',
      fio: '',
      phone: '',
      email: '',
      birthDate: '',
      mortgageSum: '',
      checkbox_ads: 'false',
      isCitizen: false,
      isMarried: false,
      hasApprovedMortgage: false
    }
  });
  const getCookieForm = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return '';
  };
  const submitData = async (data, smartToken) => {
    try {
      const preparedData = {
        formName,
        agreement: true,
        name: data.fio,
        phone: data.phone,
        cm_data: (COOKIE_SECURE_FORMA_29680 ? CookieStorage.get('cm_data') : getCookieForm('cm_data')) ?? '',
        comment: buildComment(data, modalFormData),
        sessionID: data.sessionID,
        checkbox_ads: data.checkbox_ads ?? 'false',
        ...(smartToken && {
          smartToken
        })
      };
      if (preparedData.hasApprovedMortgage) {
        preparedData.mortgageSum = Number(String(preparedData.mortgageSum).replace(/\D/g, ''));
      }
      await api.sendForm(preparedData);
      await api.logResponseGTM({
        event: 'event-to-gtm',
        eventCategory: 'Form',
        eventAction: 'Send',
        eventLabel: 'Mortgage'
      });
      setShowMessage(true);
      reset();
    } catch (e) {
      console.error('sendForm error', e);
    } finally {
      setFormData(null);
      setCaptchaToken('');
      setFormData(null);
      setIsLoading(false);
    }
  };
  const handleFormSubmit = async e_0 => {
    e_0.preventDefault();
    setIsLoading(isValid);
    try {
      const tokenResponse = await axios.get(`${UNIVERSAL_ADMIN.slice(0, -9)}/api/v2/get_token`);
      const sessionID = tokenResponse.data;
      await api.logDataToServer({
        formName,
        agreement: true,
        name: getValues().fio,
        phone: getValues().phone,
        cm_data: (COOKIE_SECURE_FORMA_29680 ? CookieStorage.get('cm_data') : getCookieForm('cm_data')) ?? '',
        comment: buildComment(getValues(), modalFormData),
        sessionID,
        checkbox_ads: String(getValues('checkbox_ads')) ?? 'false'
      });
      await handleSubmit(validData => {
        setFormData({
          ...validData,
          sessionID
        });
        setCaptchaVisible(true);
        setCaptchaKey(k => k + 1);
      })();
    } catch (e_1) {
      console.error('submit error', e_1);
    }
  };
  useEffect(() => {
    if (!isVisible) {
      reset();
      setShowMessage(false);
      const timer = setTimeout(onAfterClose, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible]);
  useEffect(() => {
    setValue('price', formatPrice(modalFormData.realtyCost, true));
    setValue('firstPaymentAmount', formatPrice(modalFormData.initialPayment, true));
    setValue('firstPaymentPercent', `${modalFormData.initialPaymentPercent}%`);
  }, [modalFormData]);
  useEffect(() => {
    if (formData && captchaToken) {
      const tokenForSubmission = captchaToken;
      setCaptchaToken('');
      setIsLoading(true);
      submitData(formData, tokenForSubmission);
    }
  }, [formData, captchaToken]);
  const handleChallengeHidden = useCallback(() => setCaptchaVisible(false), []);
  const hasApprovedMortgage = watch('hasApprovedMortgage');
  return <PortalSimple>
      <div className={cn(styles.overlay, {
      [styles.overlayVisible]: isVisible
    })}>
        <div ref={ref} className={cn(styles.modal, {
        [styles.modalShow]: isVisible
      })}>
          <button className={styles.close} onClick={onClose} aria-label='Закрыть' />
          <p className={styles.title}>Заявка на ипотеку</p>

          <form className={cn(styles.form, {
          [styles.showLoader]: isLoading,
          [styles.hideForm]: showMessage
        })} name='form-mortgage-call' onSubmit={handleFormSubmit}>
            <Controller name='price' control={control} render={({
            field
          }) => <div className={styles.field}>
                  <input {...field} placeholder=' ' readOnly />
                  <label>Стоимость</label>
                </div>} />

            <div className={styles.initialPaymentWrapper}>
              <Controller name='firstPaymentPercent' control={control} render={({
              field: field_0
            }) => <div className={styles.field}>
                    <input {...field_0} placeholder=' ' readOnly />
                    <label>Первоначальный взнос</label>
                  </div>} />

              <Controller name='firstPaymentAmount' control={control} render={({
              field: field_1
            }) => <div className={styles.field}>
                    <input {...field_1} placeholder=' ' readOnly />
                    <label>Первоначальный взнос</label>
                  </div>} />
            </div>

            <Controller name='fio' control={control} render={({
            field: field_2
          }) => <div className={styles.field}>
                  <input {...field_2} placeholder=' ' />
                  <label>ФИО</label>
                  {errors.fio && <span className={styles.error}>{errors.fio.message}</span>}
                </div>} />

            <Controller control={control} name='phone' rules={{
            required: true,
            pattern: fullPhonePattern
          }} render={({
            field: field_3
          }) => <div className={cn(styles.field, styles.phone)}>
                  <InputPhone testid='input-phone' name='phone' field={field_3} placeholder='+7 (ХХХ) ХХХ-ХХ-ХХ' label='+7 (ХХХ) ХХХ-ХХ-ХХ' errors={Boolean(errors.phone)} errorLabel={errors?.phone?.message} />
                </div>} />

            <Controller name='email' control={control} render={({
            field: field_4
          }) => <div className={styles.field}>
                  <input {...field_4} placeholder=' ' />
                  <label>Email</label>
                  {errors.email && <span className={styles.error}>{errors.email.message}</span>}
                </div>} />

            <Controller name='birthDate' control={control} render={({
            field: field_5
          }) => <div className={styles.field}>
                  <input {...field_5} placeholder=' ' onChange={e_2 => field_5.onChange(formatBirthDate(e_2.target.value))} onKeyDown={e_3 => {
              // фикс значение при Backspace
              if (e_3.key === 'Backspace') {
                const el = e_3.currentTarget;
                const pos = el.selectionStart ?? 0;
                if (pos > 0 && el.value[pos - 1] === '.') {
                  e_3.preventDefault();
                  const nextRaw = el.value.slice(0, pos - 1) + el.value.slice(pos);
                  const formatted = formatBirthDate(nextRaw);
                  field_5.onChange(formatted);
                  requestAnimationFrame(() => {
                    const newPos = Math.max(pos - 1, 0);
                    el.setSelectionRange(newPos, newPos);
                  });
                }
              }
            }} />
                  <label>Дата рождения</label>
                  {errors.birthDate && <span className={styles.error}>{errors.birthDate.message}</span>}
                </div>} />

            <DropDownCalc list={programTypes} disabled />

            <div className={styles.checkboxGroup}>
              <Controller name='isCitizen' control={control} render={({
              field: field_6
            }) => <label className={styles.checkboxRow}>
                    <CheckBox id='isCitizen' checked={field_6.value} onChange={e_4 => field_6.onChange(e_4.target.checked)} />
                    <span className={styles.checkboxText}>Я гражданин РФ</span>
                  </label>} />

              <Controller name='isMarried' control={control} render={({
              field: field_7
            }) => <label className={styles.checkboxRow}>
                    <CheckBox id='isMarried' checked={field_7.value} onChange={e_5 => field_7.onChange(e_5.target.checked)} />
                    <span className={styles.checkboxText}>Состою в браке</span>
                  </label>} />

              <Controller name='hasApprovedMortgage' control={control} render={({
              field: field_8
            }) => <label className={styles.checkboxRow}>
                    <CheckBox id='hasApprovedMortgage' checked={field_8.value} onChange={e_6 => field_8.onChange(e_6.target.checked)} />
                    <span className={styles.checkboxText}>Есть одобренная ипотека</span>
                  </label>} />
            </div>

            {hasApprovedMortgage && <>
                <Controller name='bankName' control={control} render={({
              field: field_9
            }) => <div className={styles.field}>
                      <input {...field_9} placeholder=' ' />
                      <label>Банк</label>
                      {errors.bankName && <span className={styles.error}>{errors.bankName.message}</span>}
                    </div>} />
                <Controller name='mortgageSum' control={control} render={({
              field: field_10
            }) => <div className={styles.field}>
                      <input {...field_10} placeholder=' ' />
                      <label>Сумма ипотеки</label>
                      {errors.mortgageSum && <span className={styles.error}>{errors.mortgageSum.message}</span>}
                    </div>} />
              </>}

            {Boolean(policyForm?.caption) && <SafeHtmlRenderer tag='div' className={styles.privacyText} content={policyForm.caption} />}

            {Boolean(policyForm?.checkbox) && <div className={styles.privacyText}>
                <Controller name='checkbox_ads' control={control} render={({
              field: field_11
            }) => <label className={styles.checkboxRow}>
                      <CheckBox id='checkbox_ads' checked={field_11.value === 'true'} onChange={() => field_11.onChange(field_11.value === 'true' ? 'false' : 'true')} />
                      <SafeHtmlRenderer tag='span' className={styles.checkboxText} content={policyForm.checkbox} />
                    </label>} />
              </div>}
            <ButtonCommon type='submit' text='Оставить заявку' className={styles.cta} />

            {/*SMARTCAPTCHA*/}
            <InvisibleSmartCaptcha key={captchaKey} sitekey={process.env.NEXT_PUBLIC_SMART_CAPTCHA} onSuccess={token => {
            setCaptchaToken(token);
            setCaptchaVisible(false);
          }} onChallengeHidden={handleChallengeHidden} visible={captchaVisible} hideShield />
          </form>

          {!showMessage && isLoading && <LoaderCircle someStyles={styles.spinner} />}
          {showMessage && <div className={styles.thankstext}>
              <p>Спасибо</p>
              <p>Мы получили ваш запрос и скоро свяжемся с вами</p>
            </div>}
        </div>
      </div>
    </PortalSimple>;
};